<template>
  <Form as="div" ref="form" class="view-edit-order-details" :validation-schema="validationSchema">
    <div class="form-cnt">
      <div class="form flex order-details">
        <BackstageInput
          :read-only-value="orderDetails.orderNumber"
          :label="$t('viewOrder.sales_order_number')"
          :read-only="true"></BackstageInput>
        <BackstageInputCalendar
          name="ship_date"
          v-if="isExistingOrder"
          v-model="orderDetails.dueDate"
          :label="$t('viewOrder.ship_date')"
          :disabled-dates="disabledDates"
          :read-only="!canEditDate"></BackstageInputCalendar>
        <div
          v-if="contacts && canEditOrder && !isExistingOrder"
          class="field-new"
          :class="[{ 'read-only': !canEditOrder || hasContactId }]">
          <label>{{ $t('viewOrder.contact') }}</label>
          <select v-model="orderDetails.contactId" :class="[{ 'with-arrow': canEditOrder }]" :disabled="!canEditOrder">
            <option v-for="contact in contacts" :key="contact.id" :value="contact.id">
              {{ `${contact.firstName} ${contact.lastName}${hasBuyerFunction(contact) ? ' (Buyer)' : ''}` }}
            </option>
          </select>
        </div>
        <BackstageInput
          v-if="canEditOrder"
          name="customer_po_number"
          v-model="orderDetails.customerPoNumber"
          :label="$t('viewOrder.customer_po_number')"
          data-cy="customer-po-number-editable"></BackstageInput>
        <BackstageInput
          v-else-if="orderDetails.customerPoNumber"
          :read-only-value="orderDetails.customerPoNumber"
          :label="$t('viewOrder.customer_po_number')"
          :read-only="true"
          data-cy="customer-po-number-read-only"></BackstageInput>
        <BackstageInput
          :read-only-value="formatDateNoUTC(orderDetails.date)"
          :label="$t('viewOrder.order_date')"
          :read-only="true"></BackstageInput>
        <BackstageInput
          :read-only-value="$t(`viewOrders.order_type['${orderDetails.type.toLocaleLowerCase()}']`)"
          :label="$t('viewOrder.sales_order_type')"
          :read-only="true"></BackstageInput>
        <BackstageInput
          :class="{ hold: isHoldOrder }"
          :read-only-value="$t(`viewOrders.order_status['${orderDetails.status.toLocaleLowerCase()}']`)"
          :label="$t('viewOrder.status')"
          :read-only="true"></BackstageInput>
        <div v-if="orderDetails.trackingNumbers && orderDetails.trackingNumbers.length > 0" class="field">
          <label>{{ $t('viewOrder.tracking_codes') }}</label>
          <span
            v-for="trackingObject in orderDetails.trackingNumbers"
            :key="trackingObject.trackingNumber"
            class="tracking-object">
            {{ trackingObject.trackingNumber }}
          </span>
        </div>
      </div>
      <div v-if="canEditAddress" class="address-info">
        <h5 class="title">
          {{ $t('viewOrder.shipping_to') }}
        </h5>
        <div class="radio-new" data-cy="default-address-radio" :class="[{ disabled: !!oneTimeAddress }]">
          <input
            v-model="selectedAddress"
            id="default"
            value="default"
            type="radio"
            name="address"
            :disabled="!!oneTimeAddress" />
          <label for="default">{{ $t('viewOrder.default_address') }}</label>
        </div>
        <div v-if="!oneTimeAddress" class="or">
          <span>{{ $t('viewOrder.default_or') }}</span>
        </div>
        <BackstageButton
          v-if="!oneTimeAddress"
          :variant="ButtonVariant.SECONDARY"
          data-cy="one-time-address-button-cnt"
          data-cy-button="one-time-address-button"
          v-on:click="
            $event.stopPropagation();
            toggleEditAddress();
          ">
          {{ $t('viewOrder.ship_to_one_time_address_btn') }}
        </BackstageButton>
        <div v-if="oneTimeAddress" class="radio-new" data-cy="one-time-address-radio">
          <input id="oneTimeAddress" v-model="selectedAddress" value="oneTimeAddress" type="radio" name="address" />
          <label for="oneTimeAddress" v-html="oneTimeAddress"></label>
          <i class="ico ico-edit" data-cy="one-time-address-edit-icon" v-on:click="toggleEditAddress()"></i>
          <i class="ico ico-delete" data-cy="one-time-address-delete-icon" v-on:click="removeDeliveryAddress()"></i>
        </div>
      </div>
      <div v-if="showDisabledDropshipNote" class="order-note">
        {{ $t('viewOrder.dropship_disabled_for_account_note') }}
      </div>
      <div v-if="isExistingOrder && hasDocuments(orderDetails, $t, userLocale)" class="order-documents">
        <BackstageInput
          v-if="orderDetails.documentStatus"
          :class="{ hold: isHoldOrder }"
          :read-only-value="orderDetails.documentStatus"
          :label="$t('viewOrder.sales_order_document_status')"
          :read-only="true"></BackstageInput>
        <grid-order-document :v-if="orderDetails" :order="orderDetails" />
      </div>
    </div>
  </Form>
  <NotificationMessage
    v-if="showEditAddressForm"
    :title="$t('viewOrder.one_time_address_title')"
    :size="'small'"
    v-on:close="toggleEditAddress()">
    <add-edit-delivery-address
      :delivery-address="orderDetails.deliveryAddress"
      v-on:cancel="toggleEditAddress()"
      v-on:delivery-address-updated="updateDeliveryAddress"></add-edit-delivery-address>
  </NotificationMessage>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { DraftInitInfoDto, OrderStatus, OrderType, OrderTypeDto } from '@onrunning/backstage-models';
import { ordersMixins } from '../../mixins/orders.mixins';
import { Order } from '../../models/order.model';
import { FeaturesService } from '../../services/features.service';
import { Feature } from '../../models/feature.model';
import { SFContactModel } from '../../models/sf-contact.model';
import moment from 'moment';
import { countriesWithStates, globalConstants, type State } from '../../constants/global.constants';
import addEditDeliveryAddress from '../orders/add-edit-delivery-address.vue';
import NotificationMessage from '@/components/common/NotificationMessage.vue';
import gridOrderDocument from './grid-order-document.vue';
import { Address } from '../../models/address.model';
import { DraftsService } from '../../services/drafts.service';
import { useUserStore } from '../../stores/user';
import { storeToRefs } from 'pinia';
import { toRawDeep } from '../../composables/to-raw-deep';
import { Form } from 'vee-validate';
import BackstageInput from '@/components/common/BackstageInput.vue';
import BackstageButton from '@/components/common/BackstageButton.vue';
import BackstageInputCalendar from '@/components/common/BackstageInputCalendar.vue';
import { type AnyObject, object, string } from 'yup';
import structuredClone from '@ungap/structured-clone';
import { useFormat } from '../../composables/format';
import { useLocale } from '../../composables/locale';
import { ButtonVariant } from '@/models/button-variant.enum';

const defaultAddressValue = 'default';
const oneTimeAddressValue = 'oneTimeAddress';

export default defineComponent({
  name: 'view-edit-order-details',
  components: {
    NotificationMessage,
    addEditDeliveryAddress,
    gridOrderDocument,
    // eslint-disable-next-line vue/no-reserved-component-names
    Form,
    BackstageInput,
    BackstageButton,
    BackstageInputCalendar,
  },
  props: {
    order: {
      type: Object,
      default: undefined,
    },
    maximumDate: {
      type: Date,
      default: undefined,
    },
  },
  emits: ['order-valid', 'order-updated'],
  setup() {
    const { selectedAccount } = storeToRefs(useUserStore());
    const { hasBuyerFunction, isSalesRep, isDealer, userProfile } = useUserStore();
    const { userLocale } = useLocale();
    const { formatDateNoUTC } = useFormat();

    return {
      account: selectedAccount,
      userLocale,
      ButtonVariant,
      hasBuyerFunction,
      isSalesRep,
      isDealer,
      userProfile,
      formatDateNoUTC,
    };
  },
  data() {
    const orderDetails = structuredClone(toRawDeep(this.order)) as Order;
    const selectedAddress = orderDetails.deliveryAddress?.name ? oneTimeAddressValue : defaultAddressValue;

    return {
      dropshipEnabled: false,
      editAddressButtonToggled: false,
      contacts: undefined as SFContactModel[] | undefined,
      disabledDates: undefined as any,
      orderDetails,
      featuresService: new FeaturesService(),
      draftsService: new DraftsService(),
      poNumberMaxChars: 20,
      selectedAddress,
      hasContactId: false,
    };
  },
  computed: {
    isExistingOrder(): boolean {
      return !this.theOrderIsDraft(this.orderDetails);
    },
    canEditOrder(): boolean {
      return (
        this.theOrderIsDraft(this.orderDetails) ||
        (this.isSalesRep() && this.isOrderEditableForSalesReps(this.orderDetails)) ||
        (this.isDealer() && this.isOrderEditableForDealer(this.orderDetails, this.userProfile?.user))
      );
    },
    canEditAddress(): boolean {
      return (
        this.theOrderIsDraft(this.orderDetails) &&
        this.dropshipEnabled &&
        this.orderDetails.type.toLocaleLowerCase() === OrderType.ReOrder.toLocaleLowerCase()
      );
    },
    canEditDate(): boolean {
      return this.theOrderIsDraft(this.orderDetails);
    },
    showEditAddressForm(): boolean {
      return this.canEditAddress && this.editAddressButtonToggled && !!this.orderDetails?.deliveryAddress;
    },
    oneTimeAddress(): string | undefined {
      return this.canEditAddress &&
        !!this.orderDetails?.deliveryAddress?.city &&
        !!this.orderDetails?.deliveryAddress?.zipCode &&
        !!this.orderDetails?.deliveryAddress?.street &&
        !!this.orderDetails?.deliveryAddress?.name
        ? `${this.orderDetails?.deliveryAddress?.name} <br/>${
            this.orderDetails?.deliveryAddress?.street
          }, ${this.orderDetails?.deliveryAddress?.zipCode}<br/>${
            this.orderDetails?.deliveryAddress?.state ? this.orderDetails?.deliveryAddress?.state + ' - ' : ''
          }${this.orderDetails?.deliveryAddress?.city} - ${this.orderDetails?.deliveryAddress?.country}`
        : undefined;
    },
    showDisabledDropshipNote(): boolean {
      return (
        !this.dropshipEnabled &&
        this.canEditOrder &&
        this.orderDetails?.type.toLocaleLowerCase() === OrderType.ReOrder.toLocaleLowerCase()
      );
    },
    isHoldOrder(): boolean {
      return this.orderDetails?.status === OrderStatus.Hold;
    },
    isReOrder(): boolean {
      return this.orderDetails.type.toLocaleLowerCase() === OrderType.ReOrder.toLocaleLowerCase();
    },
    validationSchema(): AnyObject {
      return object({
        customer_po_number: string().max(
          20,
          this.$t('formsValidations.rules_error_messages.max_chars', {
            field: this.$t('formsValidations.fields.customer_po_number'),
            max_chars: 20,
          }),
        ),
      });
    },
  },
  watch: {
    'orderDetails.customerPoNumber': {
      async handler() {
        const result = (await (this.$refs as any).form?.validate()) as any;
        this.$emit('order-valid', result.valid);
      },
    },
    orderDetails: {
      handler(newValue) {
        const customerPoNumberlength = this.orderDetails.customerPoNumber?.length ?? 0;

        if (customerPoNumberlength <= this.poNumberMaxChars) {
          this.$emit('order-updated', newValue);
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    ...ordersMixins,
    async init() {
      await this.setDropshipEnabled();
      this.hasContactId = this.contacts?.map((c) => c.id).includes(this.orderDetails?.contactId ?? '') ?? false;
      const draftInitInfoResult = await this.draftsService.getInitInfo(this.account.id);

      if (!draftInitInfoResult.isValid() || !draftInitInfoResult.model) {
        // show error
        return;
      }

      const selectedOrderType = draftInitInfoResult.model?.orderTypes.find(
        (orderType: OrderTypeDto) => orderType.type.toLocaleLowerCase() === this.orderDetails?.type.toLocaleLowerCase(),
      );

      this.disabledDates = (target: Date) => {
        const endDate = selectedOrderType?.endDate
          ? moment(selectedOrderType.endDate).toDate()
          : globalConstants.maximumDate;

        return target < moment(selectedOrderType?.startDate).toDate() || target > endDate;
      };

      if (this.isSalesRep()) {
        this.loadContacts(draftInitInfoResult.model);
      }
    },
    async setDropshipEnabled() {
      this.dropshipEnabled = !(await this.featuresService.hasFeature(
        Feature.DROPSHIP_ORDERS_DISABLED,
        this.account.id,
      ));
    },
    async loadContacts(draftInitInfo: DraftInitInfoDto) {
      this.contacts = draftInitInfo.userContacts;
    },
    states(): State[] {
      const selectedCountry = this.account.billingCountry?.toLowerCase();

      if (!Object.keys(countriesWithStates).includes(selectedCountry)) {
        return [];
      }

      return countriesWithStates[selectedCountry as keyof typeof countriesWithStates];
    },
    toggleEditAddress() {
      if (!this.editAddressButtonToggled && !this.orderDetails.deliveryAddress?.name) {
        this.orderDetails.deliveryAddress = new Address();
        this.orderDetails.deliveryAddress.country = this.account.billingCountry;
      }

      this.editAddressButtonToggled = !this.editAddressButtonToggled;
    },
    updateDeliveryAddress(deliveryAddress?: Address) {
      this.orderDetails.deliveryAddress = deliveryAddress;
      this.selectedAddress = oneTimeAddressValue;
      this.$emit('order-updated', this.orderDetails);
      this.toggleEditAddress();
    },
    removeDeliveryAddress() {
      this.orderDetails.deliveryAddress = undefined;
      this.selectedAddress = defaultAddressValue;
      this.$emit('order-updated', this.orderDetails);
    },
  },
});
</script>
<style scoped lang="scss">
.view-edit-order-details {
  .form-cnt {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    flex-wrap: wrap;

    .form {
      &.flex {
        display: grid;
        grid-template-columns: repeat(auto-fill, 250px);
        justify-content: space-between;
        grid-gap: 20px;
        row-gap: 20px;
        flex: 1;

        .field,
        .field-new {
          width: 250px;
        }
      }
    }

    .order-details {
      @include container-background;
      align-items: flex-start;
    }

    .address-info:deep(),
    .order-documents:deep() {
      @include container-background;
      width: 400px;
      justify-content: center;

      .title {
        font-size: 12px;
        text-align: left;
        margin: 0 0 12px;
        font-weight: 400;
        color: var(--primary-label-input-color);
      }

      .or {
        width: 100%;
        height: 1px;
        background-color: var(--border-inactive-color);
        position: relative;
        margin: 21px 0;

        span {
          display: block;
          padding: 0 10px;
          background: var(--primary-bg-color);
          color: var(--primary-text-remark-color);
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          font-size: 12px;
        }
      }

      .radio-new {
        width: 100%;

        i.ico {
          width: 24px;
          height: 24px;
          cursor: pointer;
        }
      }

      .button-container {
        width: 100%;

        button {
          width: 100%;
          justify-content: center;
        }
      }
    }

    .tracking-object {
      margin-right: 5px;
    }

    .hold {
      color: var(--special-red-color-darker);
    }

    .order-note {
      padding-top: 30px;
      border-top: 1px var(--gray-color)-border solid;
    }
  }
  select {
    &:disabled {
      color: var(--primary-text-input-color);
    }
  }
}
</style>
